import firebase from "~/firebase/clientApp";
import { v4 as uuidv4 } from "uuid";
import {
  checkDomainAvailability as cloudFunctionCheckDomainAvailability,
  createShop as cloudFunctionCreateShop,
} from "~/actions/CloudFunctions";

const FIRESTORE_COLLECTION_USERS = "users";
const FIRESTORE_COLLECTION_SHOPS = "shops";
const FIRESTORE_COLLECTION_SHOP_ITEMS = "items";
const FIRESTORE_COLLECTION_SHOP_ROLES = "roles";
const FIREBASE_DOCUMENT_DATA = "data";
const FIREBASE_STORAGE_SHOPS_PATH = "shops";
const FIREBASE_STORAGE_SHOPS_IMAGES_PATH = "images";
const FIREBASE_STORAGE_SHOPS_ITEMS_PATH = "items";
const DEFAULT_IMAGE_SCALE_TYPE = "contain";

// Checks the domain availability, useful before attempting to create a new shop.
// It's important to make this validation calling the cloud function instead of doing it
// with direct database calls because there is some custom logic on the backend that
// may prevent an otherwise available domain from being used.
export async function checkDomainAvailability(domain) {
  return cloudFunctionCheckDomainAvailability(domain).then(function (result) {
    return result.data.available;
  });
}

// Creates a shop with the provided onboarding data.
// Please take notice that for securyity reasons this operation is performed on the server-side
// by calling a cloud function. Only after the shop has been created does the creator have the
// possibility of modifying the shop
export async function createShop(onboardingData) {
  return cloudFunctionCreateShop(onboardingData).then(function (result) {
    return result;
  });
}

// Given a local image file and a Shop ID, uploads the image and changes the shop's logo
export async function setShopLogo(
  shopId,
  localLogoFile,
  scaleType = DEFAULT_IMAGE_SCALE_TYPE
) {
  const fileRef = firebase
    .storage()
    .ref()
    .child(FIREBASE_STORAGE_SHOPS_PATH)
    .child(shopId)
    .child(FIREBASE_STORAGE_SHOPS_IMAGES_PATH)
    .child(uuidv4());

  const metadata = {
    customMetadata: {
      uploadedBy: firebase.auth().currentUser.uid,
      shopId: shopId,
    },
  };

  return fileRef
    .put(localLogoFile, metadata)
    .then(function (snapshot) {
      return snapshot.ref.getDownloadURL();
    })
    .then(function (downloadURL) {
      return firebase
        .firestore()
        .collection(FIRESTORE_COLLECTION_SHOPS)
        .doc(shopId)
        .update({ logo: { src: downloadURL, scaleType: scaleType } });
    });
}

// Given a shopId, a shop current logo and a new scale type, updates the logo's scale type in the backend.
export async function updateShopLogoScaleType(
  shopId,
  originalLogo,
  newScaleType
) {
  return firebase
    .firestore()
    .collection(FIRESTORE_COLLECTION_SHOPS)
    .doc(shopId)
    .update({ logo: { src: originalLogo.src, scaleType: newScaleType } });
}

// Given a shopId, deletes the shop's logo.
export async function deleteShopLogo(shopId) {
  return firebase
    .firestore()
    .collection(FIRESTORE_COLLECTION_SHOPS)
    .doc(shopId)
    .update({ logo: null });
}

// Creates a new shop item for the given shopId.
export async function createShopItem(
  shopId,
  itemType,
  itemData,
  localImageFiles
) {
  if (!shopId || !itemType || !itemData)
    throw "shopId, itemType and itemData are required for creating a shop item!";

  let shopItemReference = firebase
    .firestore()
    .collection(FIRESTORE_COLLECTION_SHOPS)
    .doc(shopId)
    .collection(FIRESTORE_COLLECTION_SHOP_ITEMS)
    .doc();

  let shopItem = {
    ...itemData,
    ...{ type: itemType },
  };

  // Handle optional images that may be part of the shop item
  if (localImageFiles) {
    let imagesUrls = await Promise.all(
      localImageFiles.map((file) =>
        uploadImageOfShopItem(shopId, shopItemReference.id, file)
      )
    );

    let images = imagesUrls.map((imageUrl) => {
      return { src: imageUrl, scaleType: DEFAULT_IMAGE_SCALE_TYPE };
    });

    shopItem = {
      ...{ images: images },
      ...shopItem,
    };
  }

  return shopItemReference.set(shopItem).then(() => shopItemReference.id);
}

// Deletes an item from a given shop
export async function deleteShopItem(shopId, itemId) {
  return firebase
    .firestore()
    .collection(FIRESTORE_COLLECTION_SHOPS)
    .doc(shopId)
    .collection(FIRESTORE_COLLECTION_SHOP_ITEMS)
    .doc(itemId)
    .delete();
}

// Uploads an image of a shop's item to a given shop. Meant to be used with shop's items creation or update functions.
export async function uploadImageOfShopItem(
  shopId,
  itemId,
  localImageFile,
  onProgessUpdated = () => {}
) {
  const fileRef = firebase
    .storage()
    .ref()
    .child(FIREBASE_STORAGE_SHOPS_PATH)
    .child(shopId)
    .child(FIREBASE_STORAGE_SHOPS_ITEMS_PATH)
    .child(itemId)
    .child(uuidv4());

  const metadata = {
    customMetadata: {
      uploadedBy: firebase.auth().currentUser.uid,
      shopId: shopId,
      itemId: itemId,
    },
  };

  const uploadTask = fileRef.put(localImageFile, metadata);

  uploadTask.on("state_changed", (snapshot) => {
    // Observe state change events such as progress, pause, and resume
    // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
    let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
    onProgessUpdated(progress);
  });

  return uploadTask.then(function (snapshot) {
    return snapshot.ref.getDownloadURL();
  });
}

// Removes an image from a given shop storage. Notice: It wont't remove the image path from the shop's item, just the stored file.
export async function deleteImageOfShopItem(fileDownloadUrl) {
  const fileRef = firebase.storage().refFromURL(fileDownloadUrl);

  return fileRef.delete();
}

// Updates a shop's item details
export async function updateItemDetails(shopId, item) {
  return updateMultipleItemsDetails(shopId, [item]);
}

// Updates multiple shop's item details
export async function updateMultipleItemsDetails(shopId, items) {
  let batch = firebase.firestore().batch();

  items.forEach((item) => {
    const { id, ...otherItemAtributes } = item;

    let itemRef = firebase
      .firestore()
      .collection(FIRESTORE_COLLECTION_SHOPS)
      .doc(shopId)
      .collection(FIRESTORE_COLLECTION_SHOP_ITEMS)
      .doc(id);

    batch.update(itemRef, otherItemAtributes);
  });

  return batch.commit();
}

export async function updateShopDetails(shopId, updatedDetails) {
  // Filter the ID attribute, just in case. The ID must be stored as the document id, not as an attribute.
  const { id, ...otherAttributes } = updatedDetails;

  return firebase
    .firestore()
    .collection(FIRESTORE_COLLECTION_SHOPS)
    .doc(shopId)
    .update(otherAttributes);
}

// Retrieves all the members of a given shop id, with their role included.
export function subscribeToUsersFromShop(
  shopId,
  onUsersUpdated = () => {},
  onError = () => {}
) {
  return firebase
    .firestore()
    .collection(FIRESTORE_COLLECTION_SHOPS)
    .doc(shopId)
    .collection(FIRESTORE_COLLECTION_SHOP_ROLES)
    .doc(FIREBASE_DOCUMENT_DATA)
    .onSnapshot(
      function (snapshot) {
        let roles = new Map(Object.entries(snapshot.data()));

        let userFetchPromises = [];

        roles.forEach((userRole, userId) => {
          userFetchPromises.push(
            firebase
              .firestore()
              .collection(FIRESTORE_COLLECTION_USERS)
              .doc(userId)
              .get()
              .then((snapshot) => {
                return {
                  ...snapshot.data(),
                  ...{ id: userId, role: userRole },
                };
              })
          );
        });
        Promise.all(userFetchPromises)
          .then(function (users) {
            // Ordery alphabetically by name to avoid inconsistents returns after each run
            onUsersUpdated(
              users.sort(function (userA, userB) {
                return userA.displayName.localeCompare(userB.displayName);
              })
            );
          })
          .catch((error) => {
            onError(error);
          });
      },
      function (error) {
        onError(error);
      }
    );
}
